import { Check, CheckStatus, Match } from 'dashboard/models/check';
import { ComparisonResult } from 'dashboard/models/check-result/check-result';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { CountryCode } from 'lib/constants/locales';
import { ApiListRelationship, ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';
import { Iso8601DateTimeString } from 'lib/types';
import { VerificationStatus } from 'lib/verification';

import { VerificationTemplateVersion } from './verification-template-version';

interface VerificationCoreAttributes {
  'country-code'?: CountryCode;
  status?: VerificationStatus;
  checks?: Check[];
  'created-at': Iso8601DateTimeString;
  'completed-at'?: Iso8601DateTimeString;
  'submitted-at'?: Iso8601DateTimeString;
  'redacted-at'?: Iso8601DateTimeString;
  'parent-marked-for-cascading-redaction-at'?: Iso8601DateTimeString;
  'verification-template-name'?: string;
}

export enum LocationAccuracy {
  // "ROOFTOP" indicates that the returned result is a precise geocode for which we
  // have location information accurate down to street address precision.
  RoofTop = 'ROOFTOP',
  // "RANGE_INTERPOLATED" indicates that the returned result reflects an approximation
  // (usually on a road) interpolated between two precise points (such as intersections).
  // Interpolated results are generally returned when rooftop geocodes are unavailable
  // for a street address.
  RangeInterpolated = 'RANGE_INTERPOLATED',
  // "GEOMETRIC_CENTER" indicates that the returned result is the geometric center of a
  // result such as a polyline (for example, a street) or polygon (region).
  GeometricCenter = 'GEOMETRIC_CENTER',
  // "APPROXIMATE" indicates that the returned result is approximate.
  Approximate = 'APPROXIMATE',
}

interface VerificationAddressAttributes {
  'address-street-1'?: string;
  'address-street-2'?: string;
  'address-city'?: string;
  'address-subdivision'?: string;
  'address-postal-code'?: string;
  latitude?: number;
  longitude?: number;
  'location-accuracy'?: LocationAccuracy;
  'country-mismatch'?: boolean;
}

export interface VerificationGovernmentIdAttributes
  extends VerificationDatabaseAttributes,
    VerificationAddressAttributes {
  'document-id'?: string;
  'document-number'?: string;
  'front-photo-url'?: string;
  'back-photo-url'?: string;
  'photo-urls'?: GovernmentIdPhotoUrl[];
  files?: GovernmentIdFile[];
  'selected-id-class'?: GovernmentIdClass;
  'name-suffix'?: string;

  'id-properties'?: {
    'vehicle-classes'?: VehicleClasses;
  };
  'issue-date'?: string;
  'expiration-date'?: string;
  nationality?: CountryCode;
  sex?: string;
  remarks?: string;
  'visa-status'?: string;
  'from-persona'?: boolean;
  'from-reusable-persona'?: boolean;

  'entity-confidence-score'?: number;
  'entity-confidence-reasons'?: ConfidenceReasonId[];
  'capture-method'?: VerificationCaptureMethod;
  'capture-method-back'?: VerificationCaptureMethod;

  video?: { 'file-type': string; url: string; 'file-name': string };
}

export enum ConfidenceReasonId {
  PortraitMissing = 'portrait_missing',
  BarcodeMismatched = 'barcode_mismatched',

  BarcodeNotDetected = 'barcode_not_detected',
  Expired = 'expired',
  CompromisedImageMatched = 'compromised_image_matched',
  ReplicaDetected = 'replica_detected',
  DateInconsistency = 'date_inconsistency',
  ColorInconsistency = 'color_inconsistency',
  LayoutMismatched = 'layout_mismatched',
  PortraitAgeInconsistency = 'portrait_age_inconsistency',
  FailedAgeComparison = 'failed_age_comparison',

  ImageManipulation = 'image_manipulation',
  ExifInconsistency = 'exif_inconsistency',

  /**
   * Was changed from 'double_front_detected' to just 'double_front' at some point.
   */
  DoubleFrontDetected = 'double_front',
  Blurry = 'blurry',
  Glare = 'glare',
  ParseFailures = 'parse_failure',

  ClaimsConflict = 'claims_conflict',
  AccountComparisonFailure = 'account_comparison_failure',

  UnprocessableImage = 'unprocessable_image',
}

export interface VerificationEmailAddressAttributes extends VerificationCoreAttributes {
  'email-address'?: string;
  'num-confirmation-code-attempts'?: number;
  'confirmation-code'?: string;
}

export interface VerificationPhoneNumberAttributes
  extends VerificationCoreAttributes,
    VerificationAddressAttributes {
  'phone-number'?: string;
  'num-confirmation-code-attempts'?: number;
  'confirmation-code'?: string;
  'name-first'?: string;
  'name-last'?: string;
}

export interface VerificationAamvaAttributes extends VerificationCoreAttributes {
  'name-first'?: string;
  'name-last'?: string;
  birthdate?: string;
  'address-postal-code'?: string;
  'identification-number'?: string;
  'issuing-authority'?: string;
  'issue-date'?: string;
  'expiration-date'?: string;
}

export interface VerificationBusinessWebsiteAttributes
  extends VerificationCoreAttributes,
    VerificationAddressAttributes {
  'address-country-code'?: string;
  'business-name': string | null;
  'website-url': string | null;
  'identity-check-match-level-minimums'?: Record<string, Match>;
}

// Keep in sync with VerificationTemplate::CertificateKorea::CertificateType in
// app/models/verification_template/certificate_korea.rb
export enum KoreanCertificateType {
  Accredited = 'accredited',
  SamsungPass = 'samsung-pass',
  Pass = 'pass',
  Payco = 'payco',
  Kakao = 'kakao',
  Naver = 'naver',
}

export interface VerificationCertificateKoreaAttributes extends VerificationCoreAttributes {
  'name-first'?: string;
  'name-last'?: string;
  birthdate?: string;
  'identification-number'?: string;
  'certificate-type'?: KoreanCertificateType;
}

export interface VerificationDatabaseAttributes
  extends VerificationCoreAttributes,
    VerificationAddressAttributes {
  'name-first'?: string;
  'name-middle'?: string;
  'name-last'?: string;
  birthdate?: string;
  'social-security-number'?: string;
  'phone-number'?: string;
  'email-address'?: string;
  'identification-number'?: string;
  'identification-class'?: string;
  'identification-number-type'?: string;
}

export interface VerificationDatabaseAadhaarAttributes extends VerificationCoreAttributes {
  'name-first'?: string;
  'name-middle'?: string;
  'name-last'?: string;
  birthdate?: string;
}

export interface VerificationDatabaseStandardAttributes
  extends VerificationCoreAttributes,
    VerificationAddressAttributes {
  'name-first'?: string;
  'name-middle'?: string;
  'name-last'?: string;
  birthdate?: string;
  'phone-number'?: string;
  'email-address'?: string;
  'match-group-results'?: MatchGroupResult[];
}

export interface MatchGroupResult {
  description?: string;
  status?: CheckStatus;
  'match-requirement-results'?: MatchRequirementResult[];
}

export interface MatchRequirementResult {
  properties: string[];
  status: CheckStatus;
  'best-result': Match;
  required: boolean;
  'claimed-attributes': unknown[];
  'extracted-attributes': unknown[];
  'normalized-claimed-attributes': unknown[];
  'normalized-extracted-attributes': unknown[];
  'attribute-comparison-results': ComparisonResult[];
}

export interface PersonName {
  name_first?: string;
  name_middle?: string;
  name_last?: string;
  name_full?: string;
}

export interface AssociatedPerson {
  names?: string[] | PersonName[];
  positions?: string[];
}

export interface VerificationDatabaseBusinessAttributes
  extends VerificationCoreAttributes,
    VerificationAddressAttributes {
  'address-country-code'?: string;
  'business-name'?: string;
  'registration-number'?: string;
  'match-group-results'?: MatchGroupResult[];
  'associated-persons'?: AssociatedPerson[];
}
export interface VerificationDatabaseBusinessFootprintAttributes
  extends VerificationCoreAttributes,
    VerificationAddressAttributes {
  'address-country-code'?: string;
  'business-name'?: string;
  'match-group-results'?: MatchGroupResult[];
  'associated-persons'?: AssociatedPerson[];
}

export interface VerificationDatabaseBusinessFootprintAttributes
  extends VerificationCoreAttributes,
    VerificationAddressAttributes {
  'address-country-code'?: string;
  'business-name'?: string;
  'registration-number'?: string;
  'match-group-results'?: MatchGroupResult[];
  'associated-persons'?: AssociatedPerson[];
}

export interface VerificationDatabaseEcbsvAttributes extends VerificationCoreAttributes {
  'name-first'?: string;
  'name-middle'?: string;
  'name-last'?: string;
  birthdate?: string;
  'identification-number'?: string;
  'email-address'?: string;
  relationship?: EcbsvRelationship;
}

export enum EcbsvRelationship {
  Parent = 'parent',
  Guardian = 'guardian',
  Custodian = 'custodian',
}

export interface VerificationDatabasePhoneCarrierAttributes
  extends VerificationCoreAttributes,
    VerificationAddressAttributes {
  'phone-number'?: string;
  'name-first'?: string;
  'name-last'?: string;
  birthdate?: string;
}

export interface VerificationDatabaseSerproAttributes
  extends VerificationCoreAttributes,
    VerificationAddressAttributes {
  cpf?: string;
  'name-first'?: string;
  'name-last'?: string;
  birthdate?: string;
}

export interface VerificationDatabaseTinAttributes
  extends VerificationCoreAttributes,
    VerificationAddressAttributes {
  tin?: string;
  'name-first'?: string;
  'name-last'?: string;
  'name-business'?: string;
  'tin-type'?: string;
}

export interface VerificationDatabaseVatNumberAttributes
  extends VerificationCoreAttributes,
    VerificationAddressAttributes {
  'vat-number'?: string;
  'business-name'?: string;
  'match-group-results'?: MatchGroupResult[];
}

export interface VerificationDigitalIdFinnishTrustNetworkAttributes
  extends VerificationCoreAttributes {}

export interface VerificationDigitalIdOneIdAttributes extends VerificationCoreAttributes {}

export interface VerificationDigitalIdSerproAttributes extends VerificationCoreAttributes {
  'qr-code-image-url'?: string;
  'selfie-compare-image-url'?: string;
  cpf?: string;
  name?: string;
  birthdate?: string;
  'identification-number'?: string;
  'expiration-date'?: string;
  'issue-date'?: string;
  'selfie-comparison-error-code'?: string;
  'serpro-selfie-available'?: boolean;
  'selfie-confidence-level'?: string;
  'selfie-similarity-score'?: number;
}

export interface VerificationDigitalIdSingpassAttributes extends VerificationCoreAttributes {}

export interface VerificationDigitalIdUkSharecodeAttributes extends VerificationCoreAttributes {
  sharecode?: string;
  birthdate?: string;
  'name-full-input'?: string;
  'name-full'?: string;
  'reference-number'?: string;
  'right-to-work-details'?: string;
  'center-photo-url'?: string;
}

export interface VerificationDocumentAttributes extends VerificationCoreAttributes {}

export enum GovernmentIdPage {
  Front = 'front',
  Back = 'back',
  Barcode = 'barcode',
  PassportSignature = 'passport_signature',
}

export enum GovernmentIdPhotoType {
  ORIGINAL = 'original',
  NORMALIZED = 'normalized',
  LIVE = 'live',
}

export const PAGE_ORDER: Record<GovernmentIdPage, number> = {
  [GovernmentIdPage.Front]: 0,
  [GovernmentIdPage.Back]: 1,
  [GovernmentIdPage.Barcode]: 2,
  [GovernmentIdPage.PassportSignature]: 3,
};

export const PAGE_TO_LABEL: Record<GovernmentIdPage, string> = {
  [GovernmentIdPage.Front]: 'Front photo',
  [GovernmentIdPage.Back]: 'Back photo',
  [GovernmentIdPage.Barcode]: 'Barcode photo',
  [GovernmentIdPage.PassportSignature]: 'Signature photo',
};

export interface GovernmentIdPhotoUrl {
  page: string;
  url: string;
}

export interface VehicleClass {
  'issue-date': string; // ISO date string
  'expiration-date': string; // ISO date string
}

export interface VehicleClasses {
  [key: string]: VehicleClass; // Index signature for different vehicle classes (e.g., "B, G")
}

export interface NormalizedFile {
  url: string;
  filename: string;
  'file-type': string;
  'byte-size': number;
}

export interface GovernmentIdFile {
  page: GovernmentIdPage;
  'normalized-files': NormalizedFile[];
  'normalized-url'?: string;
}

export interface VerificationGovernmentIdNfcAttributes extends VerificationCoreAttributes {
  'name-first'?: string;
  'name-last'?: string;
  birthdate?: string;
  'identification-number'?: string;
  'issuing-country'?: CountryCode;
  'expiration-date'?: string;
  'portrait-photo-url'?: string;
  sex?: string;
}

export interface SubmittedAnswer {
  'question-id': number;
  'answer-id': string;
}

export interface Answer {
  id: number;
  answer: string;
}

export interface Question {
  id: number;
  question: string;
  answers: Answer[];
}

export interface VerificationSelfieAttributes extends VerificationCoreAttributes {
  'center-photo-url'?: string;
  'left-photo-url'?: string;
  'right-photo-url'?: string;
  'entity-confidence-reasons-public'?: FailureReason[];
  'entity-confidence-score'?: number;
  'entity-confidence-reasons'?: ConfidenceReasonSelfie[];
  'document-similarity-score'?: number;
  'from-persona'?: boolean;
  'from-reusable-persona'?: boolean;
  'capture-method'?: VerificationCaptureMethod;
  'capture-method-left'?: VerificationCaptureMethod;
  'capture-method-right'?: VerificationCaptureMethod;
  photos?: { url: string; name: SelfiePhotoName }[];
  'selfie-similarity-score-left'?: number;
  'selfie-similarity-score-right'?: number;
  video?: {
    url: string;
    'file-type': string;
    'file-name': string;
  };
}

export type SelfiePhotoName = 'photo' | 'center_photo' | 'left_photo' | 'right_photo';

export interface VerificationMobileDriversLicenseAttributes extends VerificationCoreAttributes {
  'name-first'?: string;
  'name-last'?: string;
  'address-street'?: string;
  'address-city'?: string;
  'address-state'?: string;
  'address-postal-code'?: string;
  'address-country-code'?: string;
  'identification-number'?: string;
  'expiration-date'?: string;
  'issuing-jurisdiction'?: string;
  'issuing-authority'?: string;
  birthdate?: string;
  age?: string;
  'age-is-at-least-age'?: string;
  'portrait-photo-url'?: string;
}

export enum FailureReason {
  Glare = 'glare',
  Glasses = 'glasses',
  Sunglasses = 'sunglasses',
  Blurry = 'blurry',
  MissingPortrait = 'missing_portrait',
  DoubleFront = 'double_front',
  Generic = 'generic',
}

export enum ConfidenceReasonSelfie {
  FaceMissing = 'face_missing',
  PoseFailure = 'pose_failure',
  DocumentComparisonFailure = 'document_comparison_failure',
  PoseComparisonFailure = 'pose_comparison_failure',
  PoseRepeatDetected = 'pose_repeat_detected',
  AccountComparisonFailure = 'account_comparison_failure',
}

export enum VerificationCaptureMethod {
  /**
   * @deprecated LIVE is deprecated in favor of MANUAL and AUTO but we keep it
   * around for backwards compatibility.
   * @see CaptureMethod::LIVE
   */
  Live = 'live',
  Auto = 'auto',
  Manual = 'manual',
  Upload = 'upload',
  Api = 'api',
}

export interface VerificationRelationships {
  account?: ApiRelationship<ApiModel.Account>;
  inquiry?: ApiRelationship<ApiModel.Inquiry>;
  transaction?: ApiRelationship<ApiModel.Transaction>;
  'list-matches'?: ApiListRelationship<ApiModel.ListMatch>;
  redactor?: Partial<ApiRelationship<ApiModel.User>>;
  tags?: ApiListRelationship<ApiModel.Tag>;
  document?: ApiRelationship<ApiModel.DocumentGeneric | ApiModel.DocumentGovernmentId>;
  'verification-template'?: ApiRelationship<VerificationTemplate['type']>;
  'verification-template-version'?: ApiRelationship<VerificationTemplateVersion['type']>;
}

export interface VerificationGovernmentIdRelationships extends VerificationRelationships {
  document?: ApiRelationship<ApiModel.DocumentGovernmentId>;
}

export const VERIFICATION_TEMPLATE_FIELDS: Record<string, string[]> = {
  [ApiModel.VerificationTemplateAamva]: [
    'address_postal_code',
    'birthdate',
    'expiration_date',
    'id_class',
    'id_designations',
    'identification_number',
    'issue_date',
    'issuing_authority',
    'name_first',
    'name_last',
  ],
  [ApiModel.VerificationTemplateBusinessWebsite]: [
    'business_name',
    'website_url',
    'address_street_1',
    'address_street_2',
    'address_city',
    'address_subdivision',
    'address_postal_code',
    'address_country_code',
  ],
  [ApiModel.VerificationTemplateDatabase]: [
    'birthdate',
    'country_code',
    'identification_number',
    'name_first',
    'name_middle',
    'name_last',
    'address_street_1',
    'address_street_2',
    'address_city',
    'address_subdivision',
    'address_postal_code',
    'phone_number',
    'email_address',
  ],
  [ApiModel.VerificationTemplateDatabaseSerpro]: [
    'birthdate',
    'country_code',
    'cpf',
    'name_first',
    'name_last',
    'name_full',
  ],
  [ApiModel.VerificationTemplateDatabaseStandard]: [
    'birthdate',
    'country_code',
    'name_first',
    'name_middle',
    'name_last',
    'address_street_1',
    'address_street_2',
    'address_city',
    'address_subdivision',
    'address_postal_code',
    'phone_number',
    'email_address',
  ],
  [ApiModel.VerificationTemplateDatabaseBusiness]: [
    'business_name',
    'registration_number',
    'address_street_1',
    'address_street_2',
    'address_city',
    'address_subdivision',
    'address_postal_code',
    'address_country_code',
    'associated_person_1_name_first',
    'associated_person_1_name_last',
    'associated_person_2_name_first',
    'associated_person_2_name_last',
  ],
  [ApiModel.VerificationTemplateDatabaseBusinessFootprint]: [
    'business_name',
    'address_street_1',
    'address_street_2',
    'address_city',
    'address_subdivision',
    'address_postal_code',
    'address_country_code',
  ],
  [ApiModel.VerificationTemplateDatabaseEcbsv]: [
    'name_first',
    'name_last',
    'birthdate',
    'identification_number',
  ],
  [ApiModel.VerificationTemplateDatabasePhoneCarrier]: [
    'name_first',
    'name_last',
    'address_street_1',
    'address_city',
    'address_subdivision',
    'address_postal_code',
    'phone_number',
    'birthdate',
    'country_code',
  ],
  [ApiModel.VerificationTemplateDatabaseTin]: ['name_first', 'name_last', 'name_business', 'tin'],
  [ApiModel.VerificationTemplateDatabaseVatNumber]: [
    'country_code',
    'vat_number',
    'business_name',
    'address_street_1',
    'address_street_2',
    'address_city',
    'address_subdivision',
    'address_postal_code',
  ],
  [ApiModel.VerificationTemplateDigitalIdSerpro]: ['cpf', 'qr_code_image', 'selfie_compare_image'],
  [ApiModel.VerificationTemplateDocument]: ['file'],
  [ApiModel.VerificationTemplateGovernmentId]: [
    'country_code',
    'selected_id_class',
    'front_photo',
    'back_photo',
  ],
  [ApiModel.VerificationTemplateGovernmentIdNfc]: ['document'],
  [ApiModel.VerificationTemplateSelfie]: ['center_photo', 'left_photo', 'right_photo'],
  [ApiModel.VerificationTemplatePhoneNumber]: ['phone_number'],
  [ApiModel.VerificationTemplateEmailAddress]: ['email_address'],
};

export const ALL_VERIFICATION_API_MODEL_TYPES = [
  ApiModel.VerificationAamva,
  ApiModel.VerificationBusinessWebsite,
  ApiModel.VerificationCertificateKorea,
  ApiModel.VerificationDatabase,
  ApiModel.VerificationDatabaseAadhaar,
  ApiModel.VerificationDatabaseStandard,
  ApiModel.VerificationDatabaseBusiness,
  ApiModel.VerificationDatabaseBusinessFootprint,
  ApiModel.VerificationDatabaseEcbsv,
  ApiModel.VerificationDatabasePhoneCarrier,
  ApiModel.VerificationDatabaseSerpro,
  ApiModel.VerificationDatabaseTin,
  ApiModel.VerificationDatabaseVatNumber,
  ApiModel.VerificationDigitalIdSerpro,
  ApiModel.VerificationDigitalIdUkSharecode,
  ApiModel.VerificationDocument,
  ApiModel.VerificationEmailAddress,
  ApiModel.VerificationGovernmentId,
  ApiModel.VerificationGovernmentIdNfc,
  ApiModel.VerificationMobileDriversLicense,
  ApiModel.VerificationPhoneNumber,
  ApiModel.VerificationSelfie,
];

export const VERIFICATION_API_MODEL_TYPES_WITH_QA_VERIFICATION = [
  ApiModel.VerificationDatabase,
  ApiModel.VerificationDatabaseStandard,
  ApiModel.VerificationDatabaseBusiness,
  ApiModel.VerificationDatabaseBusinessFootprint,
  ApiModel.VerificationDocument,
  ApiModel.VerificationGovernmentId,
  ApiModel.VerificationSelfie,
];

export type VerificationAamva = ApiResource<
  VerificationAamvaAttributes,
  VerificationRelationships,
  ApiModel.VerificationAamva
>;
export type VerificationBusinessWebsite = ApiResource<
  VerificationBusinessWebsiteAttributes,
  VerificationRelationships,
  ApiModel.VerificationBusinessWebsite
>;
export type VerificationCertificateKorea = ApiResource<
  VerificationCertificateKoreaAttributes,
  VerificationRelationships,
  ApiModel.VerificationCertificateKorea
>;
export type VerificationDatabase = ApiResource<
  VerificationDatabaseAttributes,
  VerificationRelationships,
  ApiModel.VerificationDatabase
>;
export type VerificationDatabaseAadhaar = ApiResource<
  VerificationDatabaseAadhaarAttributes,
  VerificationRelationships,
  ApiModel.VerificationDatabaseAadhaar
>;

export type VerificationDatabaseStandard = ApiResource<
  VerificationDatabaseStandardAttributes,
  VerificationRelationships,
  ApiModel.VerificationDatabaseStandard
>;
export type VerificationDatabaseBusiness = ApiResource<
  VerificationDatabaseBusinessAttributes,
  VerificationRelationships,
  ApiModel.VerificationDatabaseBusiness
>;
export type VerificationDatabaseBusinessFootprint = ApiResource<
  VerificationDatabaseBusinessFootprintAttributes,
  VerificationRelationships,
  ApiModel.VerificationDatabaseBusinessFootprint
>;
export type VerificationDatabaseEcbsv = ApiResource<
  VerificationDatabaseEcbsvAttributes,
  VerificationRelationships,
  ApiModel.VerificationDatabaseEcbsv
>;
export type VerificationDatabasePhoneCarrier = ApiResource<
  VerificationDatabasePhoneCarrierAttributes,
  VerificationRelationships,
  ApiModel.VerificationDatabasePhoneCarrier
>;
export type VerificationDatabaseSerpro = ApiResource<
  VerificationDatabaseSerproAttributes,
  VerificationRelationships,
  ApiModel.VerificationDatabaseSerpro
>;
export type VerificationDatabaseTin = ApiResource<
  VerificationDatabaseTinAttributes,
  VerificationRelationships,
  ApiModel.VerificationDatabaseTin
>;
export type VerificationDatabaseVatNumber = ApiResource<
  VerificationDatabaseVatNumberAttributes,
  VerificationRelationships,
  ApiModel.VerificationDatabaseVatNumber
>;
export type VerificationDigitalIdFinnishTrustNetwork = ApiResource<
  VerificationDigitalIdFinnishTrustNetworkAttributes,
  VerificationRelationships,
  ApiModel.VerificationDigitalIdFinnishTrustNetwork
>;
export type VerificationDigitalIdOneId = ApiResource<
  VerificationDigitalIdOneIdAttributes,
  VerificationRelationships,
  ApiModel.VerificationDigitalIdOneId
>;
export type VerificationDigitalIdSerpro = ApiResource<
  VerificationDigitalIdSerproAttributes,
  VerificationRelationships,
  ApiModel.VerificationDigitalIdSerpro
>;
export type VerificationDigitalIdSingpass = ApiResource<
  VerificationDigitalIdSingpassAttributes,
  VerificationRelationships,
  ApiModel.VerificationDigitalIdSingpass
>;
export type VerificationDigitalIdUkSharecode = ApiResource<
  VerificationDigitalIdUkSharecodeAttributes,
  VerificationRelationships,
  ApiModel.VerificationDigitalIdUkSharecode
>;
export type VerificationDocument = ApiResource<
  VerificationDocumentAttributes,
  VerificationRelationships,
  ApiModel.VerificationDocument
>;
export type VerificationEmailAddress = ApiResource<
  VerificationEmailAddressAttributes,
  VerificationRelationships,
  ApiModel.VerificationEmailAddress
>;
export type VerificationGovernmentId = ApiResource<
  VerificationGovernmentIdAttributes,
  VerificationGovernmentIdRelationships,
  ApiModel.VerificationGovernmentId
>;
export type VerificationGovernmentIdNfc = ApiResource<
  VerificationGovernmentIdNfcAttributes,
  VerificationRelationships,
  ApiModel.VerificationGovernmentIdNfc
>;
export type VerificationMobileDriversLicense = ApiResource<
  VerificationMobileDriversLicenseAttributes,
  VerificationRelationships,
  ApiModel.VerificationMobileDriversLicense
>;
export type VerificationPhoneNumber = ApiResource<
  VerificationPhoneNumberAttributes,
  VerificationRelationships,
  ApiModel.VerificationPhoneNumber
>;
export type VerificationSelfie = ApiResource<
  VerificationSelfieAttributes,
  VerificationRelationships,
  ApiModel.VerificationSelfie
>;
export type VerificationCore = ApiResource<
  VerificationCoreAttributes,
  VerificationRelationships,
  ApiModel.Verification
>;

export type Verification =
  | VerificationAamva
  | VerificationBusinessWebsite
  | VerificationCertificateKorea
  | VerificationDatabase
  | VerificationDatabaseAadhaar
  | VerificationDatabaseStandard
  | VerificationDatabaseBusiness
  | VerificationDatabaseBusinessFootprint
  | VerificationDatabaseEcbsv
  | VerificationDatabasePhoneCarrier
  | VerificationDatabaseSerpro
  | VerificationDatabaseTin
  | VerificationDatabaseVatNumber
  | VerificationDocument
  | VerificationDigitalIdFinnishTrustNetwork
  | VerificationDigitalIdOneId
  | VerificationDigitalIdSerpro
  | VerificationDigitalIdSingpass
  | VerificationDigitalIdUkSharecode
  | VerificationEmailAddress
  | VerificationGovernmentId
  | VerificationGovernmentIdNfc
  | VerificationMobileDriversLicense
  | VerificationPhoneNumber
  | VerificationSelfie;
